import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { LogsRowObj } from "..";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState } from "react";
import Searchbox from "components/fields/Searchbox";
import moment from "moment";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import Pagination from "components/pagination";
import FilterModal from "./FilterModal";

const columnHelper = createColumnHelper<LogsRowObj>();

/* ── Module color map ── */
const moduleColors: Record<string, { bg: string; border: string; color: string }> = {
  default: { bg: "linear-gradient(135deg,#f8fafc,#f1f5f9)", border: "#e2e8f0", color: "#475569" },
  user:    { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe", color: "#2563eb" },
  org:     { bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "#bbf7d0", color: "#15803d" },
  gateway: { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a", color: "#d97706" },
  role:    { bg: "linear-gradient(135deg,#faf5ff,#ede9fe)", border: "#ddd6fe", color: "#7c3aed" },
  client:  { bg: "linear-gradient(135deg,#fff1f2,#ffe4e6)", border: "#fecdd3", color: "#e11d48" },
};

const getModuleStyle = (module: string) => {
  if (!module) return moduleColors.default;
  const key = module.toLowerCase();
  for (const k of Object.keys(moduleColors)) {
    if (key.includes(k)) return moduleColors[k];
  }
  return moduleColors.default;
};

function AuditLogsTable(props: {
  users: any;
  tableData: any;
  fetchLogs: () => void;
  onValueChange: (value: string) => void;
  roleData?: any;
  isLoading: boolean;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  filterData: () => void;
  filter: any;
  setFilter: (e: any) => void;
  setLocalFilters?: (e: any) => void;
  setClearClick: () => void;
  localFilters?: any;
}) {
  const {
    users, filter, setFilter, setClearClick, setLocalFilters, localFilters,
    filterData, tableData, onValueChange, isLoading, page, setPage,
    totalpage, totalItems, currentPage, pageSize, setPageSize,
  } = props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  useEffect(() => {
    setData(tableData?.map((item: any) => ({ ...item })) ?? []);
  }, [tableData]);

  const handleValueChange = (e: any) => onValueChange(e);

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="alt-th">Date & Time</span>,
      cell: (info: any) => {
        const formatted = moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss");
        return (
          <div className="alt-date-cell">
            <div className="alt-date-icon">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <span className="alt-date">{formatted}</span>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("user_name", {
      id: "user_name",
      header: () => <span className="alt-th">User</span>,
      cell: (info: any) => {
        const name = info.getValue();
        return (
          <div className="alt-user-cell">
            <div className="alt-user-avatar">
              {name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <span className="alt-user-name">{name || "—"}</span>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("module", {
      id: "module",
      header: () => <span className="alt-th">Module</span>,
      cell: (info: any) => {
        const val = info.getValue();
        const style = getModuleStyle(val);
        return (
          <span
            className="alt-module-badge"
            style={{ background: style.bg, border: `1.5px solid ${style.border}`, color: style.color }}
          >
            {val || "—"}
          </span>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <span className="alt-th">Action</span>,
      cell: (info: any) => (
        <span className="alt-desc">{info.getValue() || "—"}</span>
      ),
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const hasActiveFilter = filter?.users?.length > 0 || filter?.dates;

  return (
    <div className="alt-card">
      {/* Header */}
      <div className="alt-header">
        <div className="alt-header-left">
          <div className="alt-header-icon">
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <div>
            <h3 className="alt-title">User Audit Logs</h3>
            <p className="alt-subtitle">
              {totalItems > 0
                ? `${totalItems} total event${totalItems !== 1 ? "s" : ""}`
                : "No events found"}
            </p>
          </div>
        </div>
        <div className="alt-header-right">
          {/* Active filter indicator */}
          {/* {hasActiveFilter && (
            <div className="alt-filter-active-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters active
            </div>
          )} */}
          <FilterModal
            users={users}
            setPage={setPage}
            filter={filter}
            setFilter={setFilter}
            filterData={filterData}
            setLocalFilters={setLocalFilters}
            localFilters={localFilters}
            setClearClick={setClearClick}
          />
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="alt-table-wrap">
        {isLoading ? (
          <div className="alt-loading">
            <div className="alt-spinner" />
            <p className="alt-loading-txt">Loading audit logs…</p>
          </div>
        ) : (
          <table className="alt-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`alt-thead-th${h.column.getCanSort() ? " alt-thead-th--sortable" : ""}`}
                    >
                      <div className="alt-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="alt-sort-icon">
                            {h.column.getIsSorted() === "asc" ? (
                              <BsArrowUp size={11} />
                            ) : h.column.getIsSorted() === "desc" ? (
                              <BsArrowDown size={11} />
                            ) : (
                              <RiArrowUpDownFill size={11} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr
                    key={row.id}
                    className={`alt-tbody-tr${ri % 2 !== 0 ? " alt-tbody-tr--alt" : ""}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="alt-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <div className="alt-empty">
                      <div className="alt-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                      <p className="alt-empty-title">No audit logs found</p>
                      <p className="alt-empty-sub">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="alt-pagination-wrap">
        <Pagination
          setPage={setPage}
          page={page}
          totalpage={totalpage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .alt-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .alt-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; flex-wrap: wrap;
        }
        .alt-header-left { display: flex; align-items: center; gap: 12px; }
        .alt-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #faf5ff, #ede9fe);
          border: 1.5px solid #ddd6fe;
          display: flex; align-items: center; justify-content: center;
          color: #7c3aed; flex-shrink: 0;
        }
        .alt-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height: 22px; }
        .alt-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .alt-header-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        /* Active filter badge */
        .alt-filter-active-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 8px;
          font-size: 11.5px; font-weight: 700;
          background: linear-gradient(135deg,#faf5ff,#ede9fe);
          border: 1.5px solid #ddd6fe; color: #7c3aed;
        }

        /* Table */
        .alt-table-wrap { overflow-x: auto; }
        .alt-table-wrap::-webkit-scrollbar { height: 4px; }
        .alt-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .alt-table { width: 100%; border-collapse: collapse; }

        /* Head */
        .alt-thead-th {
          padding: 12px 16px 10px;
          background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; white-space: nowrap; user-select: none;
        }
        .alt-thead-th--sortable { cursor: pointer; }
        .alt-thead-th--sortable:hover { background: #f1f5f9; }
        .alt-th-inner { display: flex; align-items: center; gap: 5px; }
        .alt-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .alt-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
        .alt-thead-th--sortable:hover .alt-sort-icon { color: #7c3aed; }

        /* Rows */
        .alt-tbody-tr { transition: background 0.12s; }
        .alt-tbody-tr:hover { background: #f8fafc; }
        .alt-tbody-tr--alt { background: #fafbfc; }
        .alt-tbody-tr--alt:hover { background: #f1f5f9; }
        .alt-tbody-td { padding: 13px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Date cell */
        .alt-date-cell { display: flex; align-items: center; gap: 8px; }
        .alt-date-icon {
          width: 28px; height: 28px; border-radius: 7px; flex-shrink: 0;
          background: linear-gradient(135deg,#faf5ff,#ede9fe);
          border: 1.5px solid #ddd6fe;
          display: flex; align-items: center; justify-content: center; color: #7c3aed;
        }
        .alt-date { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }

        /* User cell */
        .alt-user-cell { display: flex; align-items: center; gap: 8px; }
        .alt-user-avatar {
          width: 28px; height: 28px; border-radius: 7px; flex-shrink: 0;
          background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          border: 1.5px solid #bbf7d0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700; color: #15803d;
        }
        .alt-user-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }

        /* Module badge */
        .alt-module-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 7px;
          font-size: 11.5px; font-weight: 700; white-space: nowrap;
        }

        /* Description */
        .alt-desc { font-size: 13px; color: #475569; font-weight: 500; }

        /* Empty */
        .alt-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px 24px; gap: 10px;
        }
        .alt-empty-icon {
          width: 52px; height: 52px; border-radius: 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; color: #cbd5e1;
        }
        .alt-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .alt-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .alt-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px; gap: 12px;
        }
        .alt-spinner {
          width: 28px; height: 28px;
          border: 3px solid #e2e8f0; border-top-color: #7c3aed;
          border-radius: 50%; animation: altSpin 0.7s linear infinite;
        }
        @keyframes altSpin { to { transform: rotate(360deg); } }
        .alt-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .alt-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .alt-header { flex-direction: column; align-items: flex-start; }
          .alt-header-right { width: 100%; }
          .alt-thead-th, .alt-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default AuditLogsTable;